import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { CourtGridDataService } from '../court-grid-data.service';
import {
  BLOCK_INSET_HEIGHT_PX,
  BLOCK_INSET_TOP_PX,
  CourtColumnModel,
  CourtDurationOption,
  CourtGridSelectionEvent,
  DayGridModel,
} from '../court-grid.model';
import { hasOverlapWithBlockedRanges, nearestRangeAfter, nearestRangeBefore } from '../court-grid-blocked-ranges.util';
import { addMinutesIso, findSegmentAt, minutesBetween } from '../court-grid-time.util';
import { CourtDayGroupComponent } from './court-day-group/court-day-group.component';
import { FreeCellActivatedEvent, FreeCellDragStartEvent } from './court-column/court-column.component';
import { AggregatedCourseSessionDto } from '../../../../service/court-availability-service';
import { VenueTimezoneService } from '../../../../service/venue-timezone.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { leadTimeBlockedToastText, leadTimeTooltipText } from '../../../../shared/utils/format-lead-time';
import { LeadTimeNoticeComponent } from '../../../../shared/components/lead-time-notice/lead-time-notice.component';

const TIME_GUTTER_WIDTH = 50;
const HOUR_H = 60;
const MOBILE_HOUR_H = 50;
const MOBILE_CONTAINER_BREAKPOINT = 480;
const MIN_COURT_WIDTH_DAY = 96;
const MIN_COURT_WIDTH_WEEK = 38;
/** Comfortable per-court column width — courts beyond what fits at this width
 *  are paged (via the gutter's left/right court-pager arrows), not shrunk
 *  indefinitely. Matches the approved mockup's courtMinPx values exactly. */
const TARGET_COURT_WIDTH_DAY = 108;
const TARGET_COURT_WIDTH_DAY_MOBILE = 84;
const TARGET_COURT_WIDTH_WEEK = 30;
const TARGET_COURT_WIDTH_WEEK_MOBILE = 26;
/** Height (px) of the gutter's day/week-navigation arrow row. */
const NAV_ROW_HEIGHT = 34;
/** Height (px) of the gutter's court-pager arrow row. */
const PAGER_ROW_HEIGHT = 24;
/**
 * Height (px) of app-court-day-group's own sticky header (day name/date row +
 * court name row — 34px + 24px, see court-day-group.component.css), which
 * matches the gutter's own nav-row + pager-row heights so both stay aligned.
 * Every vertical pixel offset computed by THIS component (axis ticks, overlay
 * top, drag-frame writes) lives in the same coordinate space as `.grid-inner`,
 * but the actual court-column body only starts after this header — so it
 * must be added to any "top" derived from minutes-since-day-start.
 */
const DAY_HEADER_HEIGHT = NAV_ROW_HEIGHT + PAGER_ROW_HEIGHT;
/** Autoscroll-to-now: how much context to keep visible ABOVE the current
 *  time (the "now" line lands ~2 hours below the scrollport's top edge). */
const AUTO_SCROLL_CONTEXT_MINUTES = 120;
/** How often the "now" line/chip position is refreshed. */
const NOW_TICK_INTERVAL_MS = 60_000;
/** Now-chip collision rule (approved design): when the chip's vertical
 *  position lands within this many px of an hour tick, the chip renders with
 *  an EMPTY label — the line stays, but no duplicate/overlapping text. */
const NOW_CHIP_TICK_COLLISION_PX = 12;
/** Breathing room kept below the grid's legend at the initial page-scroll
 *  position when sizing the wrapper to fit the first screen. */
const WRAPPER_BOTTOM_GAP_PX = 24;
/** Never squeeze the grid below this, even on very short viewports — at some
 *  point scrolling the page beats an unusably small grid. */
const WRAPPER_MIN_HEIGHT_PX = 320;

type DragKind = 'move' | 'resize-top' | 'resize-bottom';

interface SelectionTuple {
  courtId: string;
  courtName: string;
  dateStr: string;
  startTimeUtc: string;
  durationMinutes: number;
}

/** Fully committed selection (post drag/click, nothing in-flight). */
type GridSelectionCommitted = SelectionTuple;

/** Live in-flight drag/resize state — `live` is what the overlay renders. */
interface GridSelectionDrag {
  kind: DragKind;
  live: SelectionTuple;
  isValid: boolean;
  lastValid: SelectionTuple;
}

/** Plain (non-signal) bookkeeping for the imperative pointermove hot path. */
interface DragImperativeState {
  kind: DragKind;
  pointerId: number;
  /** Distance from pointerdown Y to the overlay's top edge — used by 'move'. */
  grabOffsetY: number;
  /** Pointer Y at gesture start — used by resize deltaMinutes calc. */
  startClientY: number;
  originCourtId: string;
  originCourtName: string;
  originDateStr: string;
  originStartMs: number;
  originEndMs: number;
  /** Frozen clamp bounds for resize — nearest blocked range edges around the origin span. */
  gapLoMs: number;
  gapHiMs: number;
  /** Frozen duration options at the origin start — reused for resize-bottom (start never moves). */
  frozenDurationOptions: CourtDurationOption[];
  /** Last committed (snapped) tuple key — used to gate ngZone.run() calls to real changes. */
  lastSnappedKey: string;
  /** Per-snapped-anchor cache of duration options during a resize-top drag —
   *  avoids redoing the options scan on every frame between snap points. */
  resizeTopOptionsCache?: { anchorStartMs: number; options: CourtDurationOption[] };
}

/**
 * Root, stateful vertical time-axis court-availability grid. Replaces the
 * old horizontal `app-court-grid-view`. Supports free move-drag and
 * top/bottom edge-handle resize of the active selection via Pointer Events.
 *
 * Selection state model (see GridSelectionCommitted / GridSelectionDrag):
 * only one selection may exist; `activeSelection()` = drag?.live ?? committed.
 */
@Component({
  selector: 'app-court-time-grid',
  standalone: true,
  imports: [CourtDayGroupComponent, LeadTimeNoticeComponent],
  templateUrl: './court-time-grid.component.html',
  styleUrls: ['./court-time-grid.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourtTimeGridComponent implements OnDestroy {
  // ---- Public input surface -------------------------------------------------
  /** 1 day for Tagesansicht, 7 for Wochenansicht. */
  days = input<DayGridModel[]>([]);
  loading = input(false);
  /** Latest absolute UTC time a booking may start. null = no limit. */
  maxBookableUtc = input<Date | null>(null);
  /** Lead time in minutes, for toast/tooltip text. null = no limit configured. */
  leadTimeMinutes = input<number | null>(null);
  /** Interval granularity in minutes (matches the backend's aggregated-slots response). */
  intervalMinutes = input(30);
  /** All durations the category supports — passed straight through to CourtGridDataService. */
  availableIntervals = input<number[]>([]);
  /**
   * Duration pushed in from the sibling grid-selection-panel's slider (its
   * `durationChanged` output). When it changes and differs from the current
   * committed selection's duration, the committed selection's duration is
   * updated (if available) and re-emitted — mirrors the old component's
   * `externalDurationMinutes` contract.
   */
  externalDurationMinutes = input<number | null>(null);

  courtSlotSelected = output<CourtGridSelectionEvent>();
  /** Course block activated (not past, not already enrolled) — bubbled up from
   *  app-court-column; the parent opens the shared course-enrollment modal. */
  courseSessionClicked = output<AggregatedCourseSessionDto>();
  /** Jump to the previous day (Tagesansicht) or week (Wochenansicht) — the
   *  parent owns the actual date/refetch logic, this is a pure UI intent. */
  navigatePrev = output<void>();
  navigateNext = output<void>();

  // ---- Selection state (signals) --------------------------------------------
  private readonly committed = signal<GridSelectionCommitted | null>(null);
  private readonly drag = signal<GridSelectionDrag | null>(null);

  activeSelection = computed<SelectionTuple | null>(() => this.drag()?.live ?? this.committed());
  isDragging = computed(() => this.drag() !== null);
  isDragInvalid = computed(() => this.drag()?.isValid === false);

  // ---- Responsive geometry ----------------------------------------------------
  private readonly containerWidth = signal(0);

  private readonly isMobileWidth = computed(
    () => this.containerWidth() > 0 && this.containerWidth() < MOBILE_CONTAINER_BREAKPOINT,
  );

  pxPerMinute = computed(() => (this.isMobileWidth() ? MOBILE_HOUR_H : HOUR_H) / 60);

  private readonly isWeekView = computed(() => this.days().length > 1);

  /** Comfortable column width for the current view mode/breakpoint — courts
   *  beyond what fits at this width are paged, not shrunk indefinitely. */
  private readonly targetCourtWidth = computed(() => {
    if (this.isWeekView()) return this.isMobileWidth() ? TARGET_COURT_WIDTH_WEEK_MOBILE : TARGET_COURT_WIDTH_WEEK;
    return this.isMobileWidth() ? TARGET_COURT_WIDTH_DAY_MOBILE : TARGET_COURT_WIDTH_DAY;
  });

  /** How many courts fit at the target width given the current container width. */
  maxVisibleCourts = computed(() => {
    const dayCount = this.days().length || 1;
    const available = Math.max(0, this.containerWidth() - TIME_GUTTER_WIDTH);
    const perDay = Math.max(1, Math.floor(available / dayCount / this.targetCourtWidth()));
    return Math.max(1, Math.min(perDay, this.fullCourtOrder().length || 1));
  });

  courtColumnWidth = computed(() => {
    const width = this.containerWidth();
    const visibleCount = this.visibleCourtIds().length || 1;
    const dayCount = this.days().length || 1;
    const available = Math.max(0, width - TIME_GUTTER_WIDTH);
    const minWidth = this.isWeekView() ? MIN_COURT_WIDTH_WEEK : MIN_COURT_WIDTH_DAY;
    // Fill the available space with however many courts are actually visible
    // (paged down to maxVisibleCourts when there are more) rather than
    // shrinking indefinitely to cram every court in at once.
    return Math.max(minWidth, Math.floor(available / (visibleCount * dayCount)));
  });

  innerMinWidth = computed(
    () => TIME_GUTTER_WIDTH + this.days().length * this.visibleCourtIds().length * this.courtColumnWidth(),
  );

  // ---- Court identity / paging -------------------------------------------------
  fullCourtOrder = computed<{ courtId: string; courtName: string }[]>(() => {
    const day = this.days().find(d => d.courts.length);
    return day ? day.courts.map(c => ({ courtId: c.courtId, courtName: c.courtName })) : [];
  });

  private readonly courtOffsetSig = signal(0);

  showCourtPager = computed(() => this.fullCourtOrder().length > this.maxVisibleCourts());

  private readonly clampedCourtOffset = computed(() => {
    const maxOffset = Math.max(0, this.fullCourtOrder().length - this.maxVisibleCourts());
    return Math.max(0, Math.min(this.courtOffsetSig(), maxOffset));
  });

  courtPagerLabel = computed(() => {
    const total = this.fullCourtOrder().length;
    const count = this.maxVisibleCourts();
    const offset = this.clampedCourtOffset();
    return `Plätze ${offset + 1}–${Math.min(offset + count, total)} von ${total}`;
  });

  canPageCourtsLeft = computed(() => this.clampedCourtOffset() > 0);
  canPageCourtsRight = computed(() => this.clampedCourtOffset() + this.maxVisibleCourts() < this.fullCourtOrder().length);

  visibleCourtIds = computed<string[]>(() => {
    const all = this.fullCourtOrder();
    const count = this.maxVisibleCourts();
    const offset = this.clampedCourtOffset();
    return all.slice(offset, offset + count).map(c => c.courtId);
  });

  pageCourtsLeft(): void {
    this.courtOffsetSig.update(o => Math.max(0, o - this.maxVisibleCourts()));
  }

  pageCourtsRight(): void {
    const maxOffset = Math.max(0, this.fullCourtOrder().length - this.maxVisibleCourts());
    this.courtOffsetSig.update(o => Math.min(maxOffset, o + this.maxVisibleCourts()));
  }

  // ---- Grid model / data ---------------------------------------------------
  hasData = computed(() => this.days().some(d => d.courts.length > 0));

  // ---- Time axis --------------------------------------------------------------
  /** The day with the widest opening-hours span is used as the canonical vertical scale.
   *  Assumes all days in the grid share the same time-of-day opening hours (true for any
   *  real venue configuration) — if they genuinely differed, axis ticks would stay anchored
   *  to this one day while each column still positions correctly from its own dayStartUtc. */
  private canonicalColumn = computed<CourtColumnModel | null>(() => {
    let best: CourtColumnModel | null = null;
    let bestSpan = -1;
    for (const day of this.days()) {
      const col = day.courts.find(c => c.dayStartUtc && c.dayEndUtc);
      if (!col) continue;
      const span = new Date(col.dayEndUtc).getTime() - new Date(col.dayStartUtc).getTime();
      if (span > bestSpan) {
        bestSpan = span;
        best = col;
      }
    }
    return best;
  });

  /** Height of the court-column body only (excludes the day-group header). */
  columnBodyHeightPx = computed(() => {
    const col = this.canonicalColumn();
    if (!col) return 0;
    return minutesBetween(col.dayStartUtc, col.dayEndUtc) * this.pxPerMinute();
  });

  /** Total gutter height = header spacer + column body, so it matches a day-group's full height. */
  totalHeightPx = computed(() => DAY_HEADER_HEIGHT + this.columnBodyHeightPx());

  timeAxisTicks = computed<{ label: string; top: number }[]>(() => {
    const col = this.canonicalColumn();
    if (!col) return [];
    const startMs = new Date(col.dayStartUtc).getTime();
    const endMs = new Date(col.dayEndUtc).getTime();
    const pxPerMin = this.pxPerMinute();
    const ticks: { label: string; top: number }[] = [];
    for (let ms = startMs; ms <= endMs; ms += 60 * 60_000) {
      ticks.push({
        label: this.formatTime(new Date(ms).toISOString()),
        top: DAY_HEADER_HEIGHT + ((ms - startMs) / 60_000) * pxPerMin,
      });
    }
    return ticks;
  });

  // ---- Current-time ("now") line -----------------------------------------------
  /** Current wall-clock instant, refreshed every NOW_TICK_INTERVAL_MS so the
   *  now line/chip track real time (interval registered outside Angular,
   *  signal set inside the zone — see constructor). The one-shot autoscroll-
   *  to-now logic intentionally keeps using Date.now() directly. */
  private readonly nowMs = signal(Date.now());
  private nowTickIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Top offset (px, `.grid-inner` coordinate space) of the current-time line:
   * DAY_HEADER_HEIGHT + minutes-from-today's-dayStart × pxPerMinute — the same
   * coordinate space as overlayGeometry/timeAxisTicks. null (line hidden)
   * unless a day with isToday is rendered AND "now" lies within that day's
   * [dayStartUtc, dayEndUtc]. In week view the line spans all day-groups
   * (left: 50px → right edge) — vertical alignment across groups holds
   * because all days share the same time-of-day opening hours (see
   * canonicalColumn's note).
   */
  nowLineTop = computed<number | null>(() => {
    const nowMs = this.nowMs();
    const today = this.days().find(d => d.isToday);
    if (!today) return null;
    const column = today.courts.find(c => c.dayStartUtc && c.dayEndUtc);
    if (!column) return null;
    const dayStartMs = new Date(column.dayStartUtc).getTime();
    const dayEndMs = new Date(column.dayEndUtc).getTime();
    if (nowMs < dayStartMs || nowMs > dayEndMs) return null;
    return DAY_HEADER_HEIGHT + ((nowMs - dayStartMs) / 60_000) * this.pxPerMinute();
  });

  /** Red current-time chip in the time gutter. Empty label when the chip
   *  would land within NOW_CHIP_TICK_COLLISION_PX of an hour tick (design's
   *  collision rule — the line stays, no overlapping duplicate text). */
  nowChip = computed<{ top: number; label: string } | null>(() => {
    const top = this.nowLineTop();
    if (top === null) return null;
    const collides = this.timeAxisTicks().some(
      tick => Math.abs(tick.top - top) < NOW_CHIP_TICK_COLLISION_PX,
    );
    return { top, label: collides ? '' : this.formatTime(new Date(this.nowMs()).toISOString()) };
  });

  // ---- Selection overlay geometry (Angular-driven; overridden by direct DOM
  // writes during an active drag for a 60fps hot path — see processDragFrame).
  // top/height carry the same +1px/-2px visual inset as the segment blocks
  // (BLOCK_INSET_*_PX) — writeOverlayStyle applies the identical inset to the
  // un-inset values the drag frames pass it, so both render paths agree. ----
  overlayGeometry = computed<{ left: number; top: number; width: number; height: number } | null>(() => {
    const sel = this.activeSelection();
    if (!sel) return null;
    const column = this.columnFor(sel.dateStr, sel.courtId);
    if (!column) return null;
    const left = this.columnLeft(sel.dateStr, sel.courtId);
    if (left === null) return null;
    return {
      left,
      top:
        DAY_HEADER_HEIGHT +
        minutesBetween(column.dayStartUtc, sel.startTimeUtc) * this.pxPerMinute() +
        BLOCK_INSET_TOP_PX,
      width: this.courtColumnWidth(),
      height: sel.durationMinutes * this.pxPerMinute() - BLOCK_INSET_HEIGHT_PX,
    };
  });

  overlayLabel = computed(() => {
    const sel = this.activeSelection();
    if (!sel) return '';
    const endIso = addMinutesIso(sel.startTimeUtc, sel.durationMinutes);
    return `${this.formatTime(sel.startTimeUtc)}–${this.formatTime(endIso)}`;
  });

  // ---- View refs --------------------------------------------------------------
  private wrapperElRef = viewChild<ElementRef<HTMLElement>>('wrapperEl');
  private scrollContainerEl = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private overlayElRef = viewChild<ElementRef<HTMLElement>>('overlayEl');

  /**
   * Measured viewport-fit cap for the whole component (scrollbox + legend):
   * viewport height minus the wrapper's DOCUMENT top offset minus a small
   * bottom gap — i.e. sized so the entire grid fits on the FIRST screen,
   * without page-scrolling, no matter how much chrome (category card, tabs,
   * titles) sits above it. Document-coordinate based, so the value is stable
   * regardless of the current page scroll position (no resize-on-scroll
   * jank). null until first measured — the CSS fallback cap applies then.
   */
  wrapperMaxHeight = signal<number | null>(null);

  // ---- Imperative drag hot-path state (plain fields, not signals) -------------
  private dragImperative: DragImperativeState | null = null;
  private pendingMoveEvent: PointerEvent | null = null;
  private dragRafId = 0;
  private activeDragCleanup: (() => void) | null = null;
  /** Set when a free-cell press starts a create-drag: the browser still fires
   *  a click on that cell after pointerup, which onFreeCellActivated() must
   *  swallow exactly once (or it re-commits the minimum duration over the
   *  dragged one). */
  private suppressNextFreeCellClick = false;

  // ---- Autoscroll-to-now bookkeeping -------------------------------------------
  /** Value identity (joined dates) of the dataset last autoscrolled for — the
   *  scroll-to-now runs at most once per loaded dataset, so neither change-
   *  detection re-runs nor a refetch of the SAME range (after the user has
   *  scrolled manually) ever yank the scroll position again. */
  private lastAutoScrollKey = '';
  private autoScrollRafId = 0;

  constructor(
    private gridDataService: CourtGridDataService,
    private venueTimezone: VenueTimezoneService,
    private ngZone: NgZone,
    private toastService: ToastService,
  ) {
    // Observe the scroll container's width to drive responsive geometry —
    // replaces the old component's setTimeout(() => recalcCellWidth()).
    effect(onCleanup => {
      const elRef = this.scrollContainerEl();
      if (!elRef) return;
      const el = elRef.nativeElement;
      let ro: ResizeObserver;
      this.ngZone.runOutsideAngular(() => {
        ro = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width ?? el.clientWidth;
          this.ngZone.run(() => this.containerWidth.set(width));
        });
        ro.observe(el);
      });
      this.containerWidth.set(el.clientWidth);
      onCleanup(() => ro?.disconnect());
    });

    // Fit the whole component (grid + legend) onto the first screen: measure
    // the wrapper's document-top offset once layout exists and cap its height
    // to the viewport remainder. Re-measured on window resize and on dataset
    // changes (chrome above the grid can change height with the view mode).
    effect(onCleanup => {
      const elRef = this.wrapperElRef();
      this.days(); // re-measure when a new dataset renders
      if (!elRef) return;
      const el = elRef.nativeElement;

      const measure = () => {
        const docTop = el.getBoundingClientRect().top + window.scrollY;
        const firstScreenRemainder = window.innerHeight - docTop - WRAPPER_BOTTOM_GAP_PX;
        // Fit-on-first-screen only makes sense while the wrapper starts in
        // the upper half of the first screen. With enough chrome stacked
        // above (mobile: category + calendar + legend + tab cards), the grid
        // begins near or below the fold — capping it to the first-screen
        // remainder then collapses it toward WRAPPER_MIN_HEIGHT_PX while the
        // viewport below stays unused once the user scrolls to it. In that
        // case size it to a (near) full viewport instead, since the user
        // scrolls the page to the grid anyway.
        const available =
          firstScreenRemainder >= window.innerHeight / 2
            ? firstScreenRemainder
            : window.innerHeight - WRAPPER_BOTTOM_GAP_PX;
        const next = Math.max(WRAPPER_MIN_HEIGHT_PX, Math.floor(available));
        if (next !== this.wrapperMaxHeight()) {
          this.ngZone.run(() => this.wrapperMaxHeight.set(next));
        }
      };

      let rafId = 0;
      const onResize = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(measure);
      };
      this.ngZone.runOutsideAngular(() => {
        rafId = requestAnimationFrame(measure);
        window.addEventListener('resize', onResize);
      });
      onCleanup(() => {
        cancelAnimationFrame(rafId);
        window.removeEventListener('resize', onResize);
      });
    });

    // Autoscroll to the current time (time-of-day for non-today datasets)
    // when a FRESH dataset renders. Tracks only days() + the scroll
    // container's existence; the
    // lastAutoScrollKey guard (value identity, not array identity) makes this
    // a once-per-dataset action — resizes, court paging, selection changes or
    // an identical refetch never re-trigger it, so a user's manual scroll
    // position on the same loaded dataset is never overridden. Declared AFTER
    // the ResizeObserver effect above so containerWidth (→ pxPerMinute) is
    // already initialized on the first run.
    effect(() => {
      const days = this.days();
      const elRef = this.scrollContainerEl();
      if (!elRef || !days.some(d => d.courts.length > 0)) return;
      const key = days.map(d => d.date).join('|');
      if (key === this.lastAutoScrollKey) return;
      this.lastAutoScrollKey = key;
      untracked(() => this.scheduleAutoScrollToNow(elRef.nativeElement));
    });

    // Reconcile the committed selection against fresh data (new day/slots
    // fetch) — mirrors the old component's rebuildGrid() reconciliation.
    // Skipped while a drag is in-flight: nulling `committed`/`drag` here
    // would unmount the overlay and orphan `dragImperative`/the document
    // pointer listeners mid-gesture, silently swallowing the user's drag
    // with no feedback. finishDrag() already re-validates against the
    // latest grid model when the gesture ends, so this reconciliation only
    // needs to run between gestures.
    effect(() => {
      const grid = this.days();
      const isDragging = untracked(() => this.drag() !== null);
      if (isDragging) return;
      const committed = untracked(() => this.committed());
      if (!committed) return;
      if (!this.selectionStillValid(committed, grid)) {
        untracked(() => {
          this.committed.set(null);
          this.drag.set(null);
        });
      }
    });

    // Keep the "now" line/chip tracking real time — interval registered
    // outside Angular (no change-detection churn from the timer itself), the
    // signal write re-enters the zone so dependents update normally.
    this.ngZone.runOutsideAngular(() => {
      this.nowTickIntervalId = setInterval(() => {
        this.ngZone.run(() => this.nowMs.set(Date.now()));
      }, NOW_TICK_INTERVAL_MS);
    });

    // Sync duration changes pushed in from the sibling grid-selection-panel.
    effect(() => {
      const duration = this.externalDurationMinutes();
      if (duration == null) return;
      const committed = untracked(() => this.committed());
      if (!committed || committed.durationMinutes === duration) return;
      untracked(() => this.applyExternalDuration(committed, duration));
    });
  }

  ngOnDestroy(): void {
    this.activeDragCleanup?.();
    cancelAnimationFrame(this.dragRafId);
    cancelAnimationFrame(this.autoScrollRafId);
    if (this.nowTickIntervalId !== null) {
      clearInterval(this.nowTickIntervalId);
      this.nowTickIntervalId = null;
    }
  }

  /**
   * Scrolls the grid viewport so the current time sits comfortably in view
   * (~2 hours of context above "now"; for datasets not containing today, the
   * same wall-clock time of day), and — in week view — horizontally so
   * today's day-group starts right next to the sticky time gutter. Deferred
   * one animation frame (outside Angular) so the freshly rendered grid has
   * real layout before scrollTo() clamps against scrollHeight/scrollWidth.
   * Vertical geometry uses the component's canonical coordinate space:
   * DAY_HEADER_HEIGHT + minutes-from-dayStart × pxPerMinute.
   */
  private scheduleAutoScrollToNow(el: HTMLElement): void {
    cancelAnimationFrame(this.autoScrollRafId);
    this.ngZone.runOutsideAngular(() => {
      this.autoScrollRafId = requestAnimationFrame(() => {
        const days = this.days();
        const todayIndex = days.findIndex(d => d.isToday);
        // Datasets WITHOUT today (navigated to another day/week) still scroll
        // to the current TIME OF DAY — every refetch remounts the scrollbox
        // at scrollTop 0, and landing at 00:00 on every day switch forces the
        // user to re-scroll to business hours each time.
        const anchorDay = todayIndex >= 0 ? days[todayIndex] : days[0];
        const anchorColumn = anchorDay?.courts.find(c => c.dayStartUtc && c.dayEndUtc);
        if (!anchorColumn) return;

        const pxPerMin = this.pxPerMinute();
        const dayStartMs = new Date(anchorColumn.dayStartUtc).getTime();
        const dayEndMs = new Date(anchorColumn.dayEndUtc).getTime();
        const spanMs = dayEndMs - dayStartMs;
        if (spanMs <= 0) return;
        // Offset of "now" within the anchor day's span. For today this is the
        // literal now-position; for any other calendar day the modulo maps it
        // to the same wall-clock offset (whole-day multiples cancel out; only
        // across a DST change is it off by the shifted hour — acceptable).
        const nowOffsetMs = (((Date.now() - dayStartMs) % spanMs) + spanMs) % spanMs;
        const nowTop = DAY_HEADER_HEIGHT + (nowOffsetMs / 60_000) * pxPerMin;
        const top = Math.max(0, nowTop - AUTO_SCROLL_CONTEXT_MINUTES * pxPerMin);

        // Week view: today's day-group left offset inside the content is
        // TIME_GUTTER_WIDTH + dayIndex × groupWidth; subtracting the sticky
        // gutter's width (it permanently covers the scrollport's left 50px)
        // lands the group's left edge flush against the gutter. Weeks without
        // today start at Monday (left 0).
        const left = this.isWeekView() && todayIndex >= 0
          ? todayIndex * this.visibleCourtIds().length * this.courtColumnWidth()
          : 0;

        el.scrollTo({ top, left });
      });
    });
  }

  navPrevTitle = computed(() => (this.isWeekView() ? 'Vorherige Woche' : 'Vorheriger Tag'));
  navNextTitle = computed(() => (this.isWeekView() ? 'Nächste Woche' : 'Nächster Tag'));

  onNavPrev(): void {
    this.navigatePrev.emit();
  }

  onNavNext(): void {
    this.navigateNext.emit();
  }

  // ---- Lead-time gating (mirrors the old component's isOutOfLeadTime/notifyOutOfLeadTime) ----
  isOutOfLeadTime(startTimeUtc: string): boolean {
    const max = this.maxBookableUtc();
    if (!max) return false;
    return new Date(startTimeUtc).getTime() > max.getTime();
  }

  leadTimeTooltip(): string {
    const minutes = this.leadTimeMinutes();
    return minutes == null ? '' : leadTimeTooltipText(minutes);
  }

  private notifyOutOfLeadTime(): void {
    const minutes = this.leadTimeMinutes();
    if (minutes == null) return;
    this.toastService.info(leadTimeBlockedToastText(minutes));
  }

  // ---- Initial selection (free-cell click bubbled up through day-group) -------
  onFreeCellActivated(event: FreeCellActivatedEvent): void {
    // A mouse press-drag-release create gesture already committed via
    // finishDrag(); the browser's trailing click on the same cell must not
    // re-commit the minimum duration over the dragged one.
    if (this.suppressNextFreeCellClick) {
      this.suppressNextFreeCellClick = false;
      return;
    }
    if (this.isOutOfLeadTime(event.startTimeUtc)) {
      this.notifyOutOfLeadTime();
      return;
    }

    const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
      event.courtId,
      event.dateStr,
      event.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );
    const chosen = availableDurations.find(d => d.available);
    if (!chosen) return;

    this.drag.set(null);
    this.committed.set({
      courtId: event.courtId,
      courtName: event.courtName,
      dateStr: event.dateStr,
      startTimeUtc: event.startTimeUtc,
      durationMinutes: chosen.duration,
    });
    this.emitSelection(event.courtId, event.courtName, event.dateStr, event.startTimeUtc, chosen.duration, availableDurations);
  }

  // ---- Create-drag: mouse press on a free cell, drag down to extend, release --
  /**
   * Press-drag-release selection on free cells (mouse only — see
   * CourtColumnComponent.onFreeCellPointerDown for the touch rationale).
   * Reuses the existing 'resize-bottom' drag session anchored at the pressed
   * cell: the selection appears on press at the smallest bookable duration,
   * dragging down extends its end (snapped to actually-priced durations,
   * clamped at the next blocked range), releasing commits via finishDrag().
   * A no-movement press-release therefore commits exactly what a click
   * commits — the smallest available duration.
   */
  onFreeCellDragStarted(event: FreeCellDragStartEvent): void {
    if (this.dragImperative) return;
    if (this.isOutOfLeadTime(event.startTimeUtc)) {
      this.notifyOutOfLeadTime();
      return;
    }

    const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
      event.courtId,
      event.dateStr,
      event.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );
    const chosen = availableDurations.find(d => d.available);
    if (!chosen) return;

    // Stop text selection from the mouse drag; the trailing click is
    // swallowed via suppressNextFreeCellClick (preventDefault on pointerdown
    // does NOT suppress the click event).
    event.pointerEvent.preventDefault();
    this.suppressNextFreeCellClick = true;

    const base: SelectionTuple = {
      courtId: event.courtId,
      courtName: event.courtName,
      dateStr: event.dateStr,
      startTimeUtc: event.startTimeUtc,
      durationMinutes: chosen.duration,
    };
    // Anchor the resize delta at the initial selection's END edge rather than
    // the press point, so the selection's end tracks the pointer absolutely
    // ("release where it should end") instead of by relative displacement.
    const cellTop = (event.pointerEvent.currentTarget as HTMLElement).getBoundingClientRect().top;
    const endEdgeClientY = cellTop + chosen.duration * this.pxPerMinute();
    this.startDrag('resize-bottom', event.pointerEvent, base, 0, endEdgeClientY);
  }

  // ---- Course block activation (bubbled up through day-group) ------------------
  onCourseSessionClicked(session: AggregatedCourseSessionDto): void {
    this.courseSessionClicked.emit(session);
  }

  // ---- Move-drag: pointerdown on the selection block's body -------------------
  onSelectionBodyPointerDown(event: PointerEvent): void {
    const sel = this.activeSelection();
    if (!sel) return;
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.startDrag('move', event, sel, event.clientY - rect.top);
  }

  // ---- Resize-drag: pointerdown on a top/bottom edge handle -------------------
  onResizeHandlePointerDown(event: PointerEvent, kind: 'resize-top' | 'resize-bottom'): void {
    const sel = this.activeSelection();
    if (!sel) return;
    event.preventDefault();
    event.stopPropagation();
    this.startDrag(kind, event, sel, 0);
  }

  private startDrag(
    kind: DragKind,
    event: PointerEvent,
    base: SelectionTuple,
    grabOffsetY: number,
    // Create-drag anchors the resize delta at the selection's end edge
    // instead of the actual press point (see onFreeCellDragStarted).
    startClientYOverride?: number,
  ): void {
    // One drag at a time — a second pointer (accidental palm touch, a stray
    // finger on tablet, a fast second pointerdown with a different
    // pointerId) must not overwrite the in-flight session's imperative
    // state/listeners, which would orphan the first session's cleanup and
    // let releasing the WRONG pointer commit the other one's live position.
    if (this.dragImperative) return;

    const column = this.columnFor(base.dateStr, base.courtId);
    if (!column) return;

    const pointerId = event.pointerId;
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(pointerId);
    } catch {
      // Pointer capture is best-effort — drag still works via document listeners.
    }

    const originStartMs = new Date(base.startTimeUtc).getTime();
    const originEndMs = originStartMs + base.durationMinutes * 60_000;
    const gapLoMs = nearestRangeBefore(originStartMs, column.blockedRanges)?.endMs
      ?? new Date(column.dayStartUtc).getTime();
    const gapHiMs = nearestRangeAfter(originEndMs, column.blockedRanges)?.startMs
      ?? new Date(column.dayEndUtc).getTime();

    const frozenDurationOptions = this.gridDataService.getAvailableDurationsForCourt(
      base.courtId,
      base.dateStr,
      base.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );

    this.dragImperative = {
      kind,
      pointerId,
      grabOffsetY,
      startClientY: startClientYOverride ?? event.clientY,
      originCourtId: base.courtId,
      originCourtName: base.courtName,
      originDateStr: base.dateStr,
      originStartMs,
      originEndMs,
      gapLoMs,
      gapHiMs,
      frozenDurationOptions,
      lastSnappedKey: '',
    };

    this.drag.set({
      kind,
      live: { ...base },
      isValid: true,
      lastValid: { ...base },
    });

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      this.pendingMoveEvent = e;
      if (!this.dragRafId) {
        this.dragRafId = requestAnimationFrame(() => {
          this.dragRafId = 0;
          this.processDragFrame();
        });
      }
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      // Process the exact release coordinates before committing — a pending
      // rAF frame from the last pointermove may not have run yet, which
      // would otherwise commit a position up to one frame stale relative to
      // where the user actually released.
      this.pendingMoveEvent = e;
      this.processDragFrame();
      cleanup();
      this.finishDrag();
    };
    const onCancel = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      cleanup();
      this.abortDrag();
    };
    const cleanup = () => {
      cancelAnimationFrame(this.dragRafId);
      this.dragRafId = 0;
      this.pendingMoveEvent = null;
      this.activeDragCleanup = null;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
    this.activeDragCleanup = cleanup;

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
    });
  }

  /** Hot path — runs outside Angular, coalesced to one execution per animation frame. */
  private processDragFrame(): void {
    const e = this.pendingMoveEvent;
    const d = this.dragImperative;
    const dragValue = this.drag();
    if (!e || !d || !dragValue) return;

    if (d.kind === 'move') {
      this.processMoveFrame(e, d, dragValue);
    } else {
      this.processResizeFrame(e, d, dragValue);
    }
  }

  private processMoveFrame(e: PointerEvent, d: DragImperativeState, dragValue: GridSelectionDrag): void {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const colEl = el?.closest('[data-grid-col]') as HTMLElement | null;
    if (!colEl) return; // pointer outside any column — keep last rendered position

    const key = colEl.getAttribute('data-grid-col')!;
    const sepIdx = key.indexOf('__');
    if (sepIdx < 0) return;
    const dateStr = key.slice(0, sepIdx);
    const courtId = key.slice(sepIdx + 2);

    const column = this.columnFor(dateStr, courtId);
    if (!column) return;

    const colRect = colEl.getBoundingClientRect();
    const pxPerMin = this.pxPerMinute();
    const interval = this.intervalMinutes();
    const rawMinutes = (e.clientY - colRect.top - d.grabOffsetY) / pxPerMin;
    const snappedMinutes = Math.round(rawMinutes / interval) * interval;

    const dayStartMs = new Date(column.dayStartUtc).getTime();
    const dayEndMs = new Date(column.dayEndUtc).getTime();
    const duration = dragValue.live.durationMinutes;

    let candidateStartMs = dayStartMs + snappedMinutes * 60_000;
    const maxStartMs = dayEndMs - duration * 60_000;
    candidateStartMs = Math.max(dayStartMs, Math.min(maxStartMs, candidateStartMs));
    const candidateStartIso = new Date(candidateStartMs).toISOString();

    // Snap gate FIRST: everything below (duration-options scan, overlay
    // write, zone entry) depends only on this tuple, and the previous frame
    // already rendered exactly these values — most frames exit here without
    // re-running the options scan.
    const tupleKey = `${courtId}__${dateStr}__${candidateStartIso}__${duration}`;
    if (tupleKey === d.lastSnappedKey) return;
    d.lastSnappedKey = tupleKey;

    // Validity is more than "no overlap" — the target duration must also be
    // an actually-priced, offered option at the candidate start (mirrors the
    // check emitSelection() relies on; a raw overlap check alone can miss
    // cases like a duration not being offered near closing time).
    const optionsAtCandidate = this.gridDataService.getAvailableDurationsForCourt(
      courtId,
      dateStr,
      candidateStartIso,
      this.days(),
      this.availableIntervals(),
    );
    const isValid = !!optionsAtCandidate.find(o => o.duration === duration)?.available;

    this.writeOverlayStyle(
      this.columnLeft(dateStr, courtId) ?? 0,
      DAY_HEADER_HEIGHT + (candidateStartMs - dayStartMs) / 60_000 * pxPerMin,
      this.courtColumnWidth(),
      duration * pxPerMin,
      !isValid,
    );

    const courtName = this.courtNameFor(courtId, dateStr) ?? dragValue.live.courtName;
    const live: SelectionTuple = { courtId, courtName, dateStr, startTimeUtc: candidateStartIso, durationMinutes: duration };
    const lastValid = isValid ? live : dragValue.lastValid;

    this.ngZone.run(() => {
      this.drag.set({ kind: 'move', live, isValid, lastValid });
    });
  }

  private processResizeFrame(e: PointerEvent, d: DragImperativeState, dragValue: GridSelectionDrag): void {
    const column = this.columnFor(d.originDateStr, d.originCourtId);
    if (!column) return;

    const pxPerMin = this.pxPerMinute();
    const interval = this.intervalMinutes();
    const deltaMinutes = (e.clientY - d.startClientY) / pxPerMin;

    const dayStartMs = new Date(column.dayStartUtc).getTime();

    let candidateStartMs: number;
    let candidateEndMs: number;
    let durationOptionsAtStart: CourtDurationOption[];

    if (d.kind === 'resize-top') {
      let newStartMs = d.originStartMs + deltaMinutes * 60_000;
      newStartMs = this.snapToInterval(newStartMs, column.dayStartUtc, interval);
      newStartMs = Math.max(d.gapLoMs, Math.min(d.originEndMs - interval * 60_000, newStartMs));
      candidateStartMs = newStartMs;
      candidateEndMs = d.originEndMs;
      // Start moved — durations must be recomputed from the new anchor, but
      // only when the SNAPPED anchor actually changed (cached per anchor;
      // between snap points every frame would otherwise redo the scan).
      if (d.resizeTopOptionsCache?.anchorStartMs !== candidateStartMs) {
        d.resizeTopOptionsCache = {
          anchorStartMs: candidateStartMs,
          options: this.gridDataService.getAvailableDurationsForCourt(
            d.originCourtId,
            d.originDateStr,
            new Date(candidateStartMs).toISOString(),
            this.days(),
            this.availableIntervals(),
          ),
        };
      }
      durationOptionsAtStart = d.resizeTopOptionsCache.options;
    } else {
      let newEndMs = d.originEndMs + deltaMinutes * 60_000;
      newEndMs = this.snapToInterval(newEndMs, column.dayStartUtc, interval);
      newEndMs = Math.max(d.originStartMs + interval * 60_000, Math.min(d.gapHiMs, newEndMs));
      candidateStartMs = d.originStartMs;
      candidateEndMs = newEndMs;
      // Start is frozen for a bottom-handle resize — reuse the options computed at drag start.
      durationOptionsAtStart = d.frozenDurationOptions;
    }

    const anchorStartIso = new Date(candidateStartMs).toISOString();
    const rawDuration = Math.round((candidateEndMs - candidateStartMs) / 60_000 / interval) * interval;
    const originDuration = (d.originEndMs - d.originStartMs) / 60_000;
    const nearest = this.nearestAvailableDuration(rawDuration, durationOptionsAtStart, originDuration);

    if (!nearest) {
      // Nothing bookable in range — mark invalid but keep dragging.
      const invalidLive: SelectionTuple = {
        courtId: d.originCourtId,
        courtName: d.originCourtName,
        dateStr: d.originDateStr,
        startTimeUtc: anchorStartIso,
        durationMinutes: Math.max(interval, rawDuration),
      };
      this.writeOverlayStyle(
        this.columnLeft(d.originDateStr, d.originCourtId) ?? 0,
        DAY_HEADER_HEIGHT + (candidateStartMs - dayStartMs) / 60_000 * pxPerMin,
        this.courtColumnWidth(),
        invalidLive.durationMinutes * pxPerMin,
        true,
      );
      const tupleKey = `invalid__${invalidLive.startTimeUtc}__${invalidLive.durationMinutes}`;
      if (tupleKey === d.lastSnappedKey) return;
      d.lastSnappedKey = tupleKey;
      this.ngZone.run(() => {
        this.drag.set({ kind: d.kind, live: invalidLive, isValid: false, lastValid: dragValue.lastValid });
      });
      return;
    }

    const top = DAY_HEADER_HEIGHT + (candidateStartMs - dayStartMs) / 60_000 * pxPerMin;
    const height = nearest.duration * pxPerMin;

    this.writeOverlayStyle(this.columnLeft(d.originDateStr, d.originCourtId) ?? 0, top, this.courtColumnWidth(), height, false);

    const tupleKey = `${d.originCourtId}__${d.originDateStr}__${anchorStartIso}__${nearest.duration}`;
    if (tupleKey === d.lastSnappedKey) return;
    d.lastSnappedKey = tupleKey;

    const live: SelectionTuple = {
      courtId: d.originCourtId,
      courtName: d.originCourtName,
      dateStr: d.originDateStr,
      startTimeUtc: anchorStartIso,
      durationMinutes: nearest.duration,
    };
    this.ngZone.run(() => {
      this.drag.set({ kind: d.kind, live, isValid: true, lastValid: live });
    });
  }

  private finishDrag(): void {
    const dragValue = this.drag();
    this.dragImperative = null;

    // Safety net for the create-drag click suppression: the trailing click
    // (when it fires at all — releases outside the cell without pointer
    // capture produce none) is dispatched in the same input-task sequence as
    // pointerup, i.e. before any timer callback — so this clear can never
    // race ahead of a legitimate click, only mop up a stale flag.
    if (this.suppressNextFreeCellClick) {
      setTimeout(() => (this.suppressNextFreeCellClick = false));
    }

    if (!dragValue) return;

    if (dragValue.isValid) {
      const live = dragValue.live;
      const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
        live.courtId,
        live.dateStr,
        live.startTimeUtc,
        this.days(),
        this.availableIntervals(),
      );
      // finishDrag() is invoked from a `document` pointerup listener
      // registered via runOutsideAngular — emitSelection()'s EventEmitter.emit()
      // must happen INSIDE this same ngZone.run() call, not as a separate
      // statement after it, or the parent's (courtSlotSelected) handler runs
      // outside the zone too and its state changes (selection panel, mobile
      // summary bar, etc.) may not trigger a change-detection pass, leaving
      // the parent's UI stale until some unrelated zone-patched event fires.
      this.ngZone.run(() => {
        this.committed.set({ ...live });
        this.drag.set(null);
        this.emitSelection(live.courtId, live.courtName, live.dateStr, live.startTimeUtc, live.durationMinutes, availableDurations);
      });
    } else {
      // Invalid drop — snap back to the last valid position (or the original
      // committed selection); CSS transition on the overlay animates this.
      this.ngZone.run(() => this.drag.set(null));
    }
  }

  private abortDrag(): void {
    this.dragImperative = null;
    // A canceled pointer never produces a click — drop the suppression
    // immediately or it would swallow the next genuine activation.
    this.suppressNextFreeCellClick = false;
    this.ngZone.run(() => this.drag.set(null));
  }

  /**
   * Direct DOM write for the drag hot path. `top`/`height` are the UN-inset
   * coordinate-space values (DAY_HEADER_HEIGHT + minutes × pxPerMinute /
   * duration × pxPerMinute) — the +1px/-2px visual block inset is applied
   * HERE, exactly once, mirroring overlayGeometry. Callers must never
   * pre-apply it, and the inset never feeds back into any time math.
   */
  private writeOverlayStyle(left: number, top: number, width: number, height: number, invalid: boolean): void {
    const el = this.overlayElRef()?.nativeElement;
    if (!el) return;
    el.style.left = `${left}px`;
    el.style.top = `${top + BLOCK_INSET_TOP_PX}px`;
    el.style.width = `${width}px`;
    el.style.height = `${height - BLOCK_INSET_HEIGHT_PX}px`;
    el.classList.toggle('invalid', invalid);
  }

  private snapToInterval(candidateMs: number, dayStartIso: string, interval: number): number {
    const dayStartMs = new Date(dayStartIso).getTime();
    const minutes = Math.round((candidateMs - dayStartMs) / 60_000 / interval) * interval;
    return dayStartMs + minutes * 60_000;
  }

  /**
   * Nearest available duration to `rawDuration`. On an exact tie between two
   * candidates, breaks toward `originDuration`'s direction of travel — without
   * this, extending a selection (dragging outward) stalls for roughly half
   * the gap between offered durations whenever `availableIntervals` has gaps
   * (e.g. a category offering only 60/90/120-minute slots), since a plain
   * "keep the first/smaller candidate on tie" rule fights the drag direction.
   */
  private nearestAvailableDuration(
    rawDuration: number,
    options: CourtDurationOption[],
    originDuration?: number,
  ): CourtDurationOption | null {
    const available = options.filter(o => o.available);
    if (!available.length) return null;
    const growing = originDuration != null && rawDuration >= originDuration;
    return available.reduce((best, o) => {
      const dO = Math.abs(o.duration - rawDuration);
      const dBest = Math.abs(best.duration - rawDuration);
      if (dO < dBest) return o;
      if (dO > dBest) return best;
      if (originDuration == null) return best;
      return growing
        ? (o.duration > best.duration ? o : best)
        : (o.duration < best.duration ? o : best);
    });
  }

  private applyExternalDuration(committed: GridSelectionCommitted, duration: number): void {
    const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
      committed.courtId,
      committed.dateStr,
      committed.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );
    const option = availableDurations.find(d => d.duration === duration && d.available);
    if (!option) return;
    this.committed.set({ ...committed, durationMinutes: duration });
    this.emitSelection(committed.courtId, committed.courtName, committed.dateStr, committed.startTimeUtc, duration, availableDurations);
  }

  private emitSelection(
    courtId: string,
    courtName: string,
    dateStr: string,
    startTimeUtc: string,
    durationMinutes: number,
    availableDurations: CourtDurationOption[],
  ): void {
    const column = this.columnFor(dateStr, courtId);
    const anchorSegment = column
      ? findSegmentAt(column.segments, new Date(startTimeUtc).getTime())
      : undefined;
    const durationOption = availableDurations.find(d => d.duration === durationMinutes);
    if (!column || !anchorSegment || !durationOption) return;

    this.courtSlotSelected.emit({
      courtId,
      courtName,
      dateStr,
      startTimeUtc,
      endTimeUtc: durationOption.endTimeUtc,
      durationMinutes,
      price: durationOption.price,
      aggregatedSlot: anchorSegment.aggregatedSlot,
      availableDurations,
    });
  }

  private selectionStillValid(sel: SelectionTuple, days: DayGridModel[]): boolean {
    const day = days.find(d => d.date === sel.dateStr);
    const column = day?.courts.find(c => c.courtId === sel.courtId);
    if (!day || !column || !column.dayStartUtc || !column.dayEndUtc) return false;
    const startMs = new Date(sel.startTimeUtc).getTime();
    const endMs = startMs + sel.durationMinutes * 60_000;
    const dayStartMs = new Date(column.dayStartUtc).getTime();
    const dayEndMs = new Date(column.dayEndUtc).getTime();
    if (startMs < dayStartMs || endMs > dayEndMs) return false;
    return !hasOverlapWithBlockedRanges(startMs, endMs, column.blockedRanges);
  }

  private columnFor(dateStr: string, courtId: string): CourtColumnModel | undefined {
    return this.days().find(d => d.date === dateStr)?.courts.find(c => c.courtId === courtId);
  }

  private courtNameFor(courtId: string, dateStr: string): string | undefined {
    return this.columnFor(dateStr, courtId)?.courtName ?? this.fullCourtOrder().find(c => c.courtId === courtId)?.courtName;
  }

  /** Left offset (px) of a court column within the shared scroll area, derived
   *  purely from day/court index — no DOM measurement needed for rendering. */
  private columnLeft(dateStr: string, courtId: string): number | null {
    const dayIndex = this.days().findIndex(d => d.date === dateStr);
    if (dayIndex === -1) return null;
    const visibleIds = this.visibleCourtIds();
    const courtIndex = visibleIds.indexOf(courtId);
    if (courtIndex === -1) return null;
    const colWidth = this.courtColumnWidth();
    return TIME_GUTTER_WIDTH + dayIndex * visibleIds.length * colWidth + courtIndex * colWidth;
  }

  formatTime(isoInstant: string): string {
    try {
      return this.venueTimezone.formatTime(isoInstant);
    } catch {
      return '';
    }
  }
}
